import m, {type Children} from '@bitstillery/mithril'
import {MithrilComponent} from '@bitstillery/mithril'
import {Button} from '@bitstillery/common/components/ui/button/button'

import type {CmsComponentMeta} from '@bitstillery/common/cms/catalog_meta'

export interface PromoCardCta {
    /** Navigation target. Omit when using `onclick` (e.g. an auth-gated `require_login`). */
    href?: string
    children: Children
    /**
     * Click handler — use for an action rather than navigation, e.g. an auth-gated
     * `() => require_login('/account/preferences')`. When set, the button calls this instead of
     * navigating via `href`.
     */
    onclick?: (event?: Event) => void
}

export const promoCardMeta = {
    tier: 'content',
    summary: 'Two-panel marketing callout: a lead/message panel paired with a person/quote panel, each with an optional CTA.',
    when_to_use:
        'Pair a promotional message (newsletter sign-up, a feature pitch, an announcement) with a person/quote panel. ' +
        'No built-in defaults — pass every prop; copy may use `$t(...)`. Each CTA is `{children, href}` to navigate, or ' +
        '`{children, onclick}` for an action (e.g. an auth-gated `onclick: () => require_login(target)`). Pass `null` to omit a CTA.',
    example: `
    <PromoCard
        description="Tell us what you are looking for and we source it."
        intro_button={{children: 'Contact us', href: '/contact'}}
        profile_image={{alt: 'Cor', src: '/common/img/catalog/cor.jpg'}}
        quote="They found forty pallets in two days."
        quote_attribution="Cor, Account manager"
        quote_button={null}
        title="Sourcing on request"
    />
    `,
    variants: [
        {
            prop: 'quote_button',
            jsx: `
            <PromoCard
                description="Tell us what you are looking for and we source it."
                intro_button={{children: 'Contact us', href: '/contact'}}
                profile_image={{alt: 'Cor', src: '/common/img/catalog/cor.jpg'}}
                quote="They found forty pallets in two days."
                quote_attribution="Cor, Account manager"
                quote_button={{children: 'Read the story', href: '/about'}}
                title="Sourcing on request"
            />
            `,
        },
    ],
} satisfies CmsComponentMeta

export interface PromoCardAttrs {
    title: Children
    description: Children
    /** Lead-panel CTA — `{children, href}` to navigate or `{children, onclick}` for an action; pass `null` to omit. */
    intro_button: PromoCardCta | null
    profile_image: {src: string; alt: string}
    quote: Children
    quote_attribution: Children
    /** Quote-panel CTA — `{children, href}` to navigate or `{children, onclick}` for an action; pass `null` to omit. */
    quote_button: PromoCardCta | null
}

export class PromoCard extends MithrilComponent<PromoCardAttrs> {
    /** A CTA is link-styled; `onclick` (e.g. an auth-gated `require_login`) takes precedence over `href`. */
    private cta(cta: PromoCardCta | null) {
        if (cta == null) {
            return null
        }
        return (
            <Button
                className='c-promo-card-btn'
                link={cta.onclick ? undefined : cta.href}
                onclick={cta.onclick}
                type='info'
                variant='link'
            >
                {cta.children}
            </Button>
        )
    }

    view(vnode: m.Vnode<PromoCardAttrs>) {
        const attrs = vnode.attrs

        return (
            <section className='c-promo-card'>
                <div className='c-promo-card-content'>
                    <div className='c-promo-card-intro'>
                        <h3 className='c-promo-card-title'>{attrs.title}</h3>
                        <p className='c-promo-card-description'>{attrs.description}</p>
                        {this.cta(attrs.intro_button)}
                    </div>
                    <div className='c-promo-card-quote'>
                        <div className='c-promo-card-profile'>
                            <img
                                className='c-promo-card-profile-img'
                                src={attrs.profile_image.src}
                                alt={attrs.profile_image.alt}
                            />
                        </div>
                        <div className='c-promo-card-quote-content'>
                            <blockquote className='c-promo-card-quote-text'>&ldquo;{attrs.quote}&rdquo;</blockquote>
                            <cite className='c-promo-card-attribution'>{attrs.quote_attribution}</cite>
                            {this.cta(attrs.quote_button)}
                        </div>
                    </div>
                </div>
            </section>
        )
    }
}
